/*
 * Microphone capture for pronunciation training — records, and hands back the one audio format
 * every speech API in the evaluation asks for: 16 kHz mono 16-bit PCM WAV.
 * docs/project/ai-features-plan.md §1.
 *
 * WHY THE CONVERSION HAPPENS HERE AND NOT ON THE SERVER
 * MediaRecorder produces WebM/Opus, and no JVM decodes Opus without a native library — a
 * server-side conversion would mean putting ffmpeg on the box and keeping it there. The browser
 * already owns a decoder and a resampler (Web Audio), so the page does the work and posts a
 * finished WAV. The endpoint then takes plain PCM from anyone, which is also what lets the future
 * mobile clients use it with nothing installed anywhere.
 *
 * A word on the capture settings: echo cancellation, noise suppression and auto gain are left ON.
 * They are what makes a laptop microphone in a normal room usable at all, and the assessment
 * models are trained on ordinary recordings rather than clean-room ones. Turning them off to
 * "preserve the signal" makes scores worse, not truer.
 *
 * No UI in this file on purpose. The look and feel of the mic control is an open owner decision
 * (docs/project/ai-features-plan.md §1, step 3); this is the part that will not change whatever
 * gets decided, so it lands separately and the UI is written against it.
 */
(function (global) {
    'use strict';

    var SAMPLE_RATE = 16000;

    /* Anything below this peak is a room, not a person: a muted mic, a device the browser opened
       but nothing is plugged into, or someone who did not actually speak. Worth catching in the
       page, because sending it costs a call and comes back as "recognised no speech", which reads
       to a learner like a verdict on their pronunciation rather than on their microphone. */
    var SILENCE_PEAK = 0.01;

    function isSupported() {
        return !!(global.navigator && global.navigator.mediaDevices
            && global.navigator.mediaDevices.getUserMedia
            && global.MediaRecorder
            && (global.AudioContext || global.webkitAudioContext)
            && (global.OfflineAudioContext || global.webkitOfflineAudioContext));
    }

    function writeString(view, offset, text) {
        for (var i = 0; i < text.length; i++) {
            view.setUint8(offset + i, text.charCodeAt(i));
        }
    }

    /* Canonical 44-byte RIFF header + signed 16-bit little-endian samples. The server reads this
       header back and refuses anything that is not exactly this, so the two halves are written
       against the same spec deliberately (see WavAudio.java). */
    function encodeWav(samples, sampleRate) {
        var bytes = samples.length * 2;
        var view = new DataView(new ArrayBuffer(44 + bytes));
        writeString(view, 0, 'RIFF');
        view.setUint32(4, 36 + bytes, true);
        writeString(view, 8, 'WAVE');
        writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true);              // fmt chunk length
        view.setUint16(20, 1, true);               // 1 = uncompressed PCM
        view.setUint16(22, 1, true);               // mono
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * 2, true);  // byte rate = rate * channels * bytesPerSample
        view.setUint16(32, 2, true);               // block align
        view.setUint16(34, 16, true);              // bits per sample
        writeString(view, 36, 'data');
        view.setUint32(40, bytes, true);
        var off = 44;
        for (var i = 0; i < samples.length; i++, off += 2) {
            /* Clamp before scaling: a sample of exactly 1.0 scaled by 0x8000 wraps to full-scale
               NEGATIVE as a signed 16-bit int, which is an audible click on every peak. */
            var s = Math.max(-1, Math.min(1, samples[i]));
            view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
        return view.buffer;
    }

    function peakOf(samples) {
        var peak = 0;
        for (var i = 0; i < samples.length; i++) {
            var v = samples[i] < 0 ? -samples[i] : samples[i];
            if (v > peak) { peak = v; }
        }
        return peak;
    }

    /* Decode whatever MediaRecorder produced, then re-render it through an offline context that
       runs at 16 kHz — which is both the resample and the mixdown to mono in one pass, done by the
       browser's own resampler rather than by hand.

       The ORIGINAL blob travels back beside the WAV (added with the UI, 2026-08-20): the 16 kHz
       mono downmix is Azure's format, not a listening format, and "hear your attempt" played from
       it would make every learner sound worse than they are. The original never leaves the page —
       it is not uploaded, not stored, and dropped when the popup closes. */
    function toWav(blob) {
        var Ctx = global.AudioContext || global.webkitAudioContext;
        var Offline = global.OfflineAudioContext || global.webkitOfflineAudioContext;
        /* The decode context is PINNED to 16 kHz rather than left at the output device's rate.
           decodeAudioData resamples into its context's rate, so this makes the offline render a
           same-rate pass — removing the one cross-rate seam in the chain. Measured reason: a
           session produced clips whose waveform ran ~3.5× slower than speech (loud rumble, Azure
           heard nothing), the signature of a rate mislabel across that seam. */
        var ctx;
        try {
            ctx = new Ctx({ sampleRate: SAMPLE_RATE });
        } catch (e) {
            ctx = new Ctx();   // older engines: keep the device-rate context rather than fail
        }
        return blob.arrayBuffer()
            .then(function (buf) { return ctx.decodeAudioData(buf); })
            .then(function (decoded) {
                ctx.close();
                var frames = Math.max(1, Math.ceil(decoded.duration * SAMPLE_RATE));
                var offline = new Offline(1, frames, SAMPLE_RATE);
                var src = offline.createBufferSource();
                src.buffer = decoded;
                src.connect(offline.destination);
                src.start(0);
                return offline.startRendering();
            })
            .then(function (rendered) {
                var samples = rendered.getChannelData(0);
                return {
                    wav: encodeWav(samples, SAMPLE_RATE),
                    blob: blob,
                    seconds: rendered.duration,
                    peak: peakOf(samples)
                };
            });
    }

    function Recorder() {
        this.stream = null;
        this.recorder = null;
        this.chunks = [];
        this.pending = null;   // identifies the hold currently waiting for the device to open
    }

    function hasLiveTrack(stream) {
        return !!(stream && stream.getTracks().some(function (t) {
            return t.readyState === 'live';
        }));
    }

    /**
     * Opens the microphone WITHOUT recording. The popup calls this the moment it appears, so the
     * device wake-up — driver init plus the echo-cancel/AGC ramp, which repeatedly clipped the
     * FIRST attempt into "nothing was heard" — happens while the learner is still reading the
     * word, never while they are speaking. This is also where the browser's permission prompt
     * shows up, which is likewise better before a hold than during one. Idempotent while the
     * stream is live; a stream whose device went away is dropped and reopened.
     */
    Recorder.prototype.open = function () {
        var self = this;
        if (hasLiveTrack(this.stream)) { return Promise.resolve(true); }
        this.release();
        return global.navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        }).then(function (stream) {
            self.stream = stream;
            return true;
        });
    };

    /** Starts recording on the already-open stream — opening it first if nothing warmed it. */
    Recorder.prototype.start = function () {
        var self = this;
        var hold = {};
        this.pending = hold;
        return this.open().then(function () {
            if (self.pending !== hold) {
                return false;   // the hold ended (or was abandoned) before the device opened
            }
            self.pending = null;
            self.chunks = [];
            self.recorder = new MediaRecorder(self.stream);
            self.recorder.ondataavailable = function (e) {
                if (e.data && e.data.size > 0) { self.chunks.push(e.data); }
            };
            self.recorder.start();
            return true;
        });
    };

    Recorder.prototype.isRecording = function () {
        return !!(this.recorder && this.recorder.state === 'recording');
    };

    /**
     * Stops, releases the microphone, and resolves with
     * {wav: ArrayBuffer, blob: Blob, seconds: Number, peak: Number, silent: Boolean}.
     * `wav` is what the server takes; `blob` is the untouched recording, for playback only.
     */
    Recorder.prototype.stop = function () {
        var self = this;
        if (!this.recorder) {
            return Promise.reject(new Error('not recording'));
        }
        return new Promise(function (resolve, reject) {
            self.recorder.onstop = function () {
                /* The STREAM stays open: a hold after the first must never re-pay the device
                   wake-up that clipped first attempts into "nothing was heard". The recording
                   indicator therefore stays lit while the popup is up — truthfully, since this
                   surface IS a microphone — and goes out when the popup closes and releases the
                   device. (This replaces the early release that killed the indicator during
                   scoring at the price of a cold device on every hold.) */
                self.recorder = null;
                var type = self.chunks.length ? self.chunks[0].type : 'audio/webm';
                toWav(new Blob(self.chunks, { type: type }))
                    .then(function (result) {
                        result.silent = result.peak < SILENCE_PEAK;
                        resolve(result);
                    })
                    .catch(reject);
            };
            self.recorder.stop();
        });
    };

    /**
     * Abandons an in-flight hold: recording stops, nothing is decoded, the stream stays warm for
     * the next hold. (Full device release is {@code release()}, the popup-close path.)
     */
    Recorder.prototype.cancel = function () {
        this.pending = null;
        if (this.recorder && this.recorder.state !== 'inactive') {
            this.recorder.ondataavailable = null;
            this.recorder.onstop = null;
            try { this.recorder.stop(); } catch (e) { /* already stopped */ }
        }
        this.recorder = null;
        this.chunks = [];
    };

    /** Safe to call at any point, including from an error path or a page teardown. */
    Recorder.prototype.release = function () {
        if (this.stream) {
            this.stream.getTracks().forEach(function (t) { t.stop(); });
            this.stream = null;
        }
        this.recorder = null;
    };

    global.pronounceRecorder = {
        isSupported: isSupported,
        create: function () { return new Recorder(); },
        SAMPLE_RATE: SAMPLE_RATE
    };
})(window);
